import React, { useEffect, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Form, Button, Card, Row, Col } from 'react-bootstrap'
import { Table as SuperTable, Thead, Tbody, Tr, Th, Td } from 'react-super-responsive-table'
import axios from 'axios'
import Cookies from 'js-cookie'
import { BASE_URL } from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import {
  BsFunnelFill,
  BsSearch,
  BsArrowLeft,
  BsPersonPlusFill,
  BsCheckCircleFill,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import SelectableField from './commonComponents/SelectableField'

const AssignBustoStudent = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()

  const bus = location.state?.bus || null

  const [students, setStudents] = useState([])
  const [filteredStudents, setFilteredStudents] = useState([])
  const [selectedStudents, setSelectedStudents] = useState([])
  const [loading, setLoading] = useState(false)
  const [assigning, setAssigning] = useState(false)
  const [search, setSearch] = useState('')
  const [selectedData, setSelectedData] = useState({})
  const [resetKey, setResetKey] = useState(0)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const [showFilterCard, setShowFilterCard] = useState(true)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1

        setMarginLeft(
          isCollapsed ? `${collapsedWidth + gap}px` : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }

      setIsMobile(window.innerWidth <= 768)
    }

    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  useEffect(() => {
    fetchAllStudents()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchAllStudents = async () => {
    setLoading(true)
    try {
      const institueId = Cookies.get('institute_id')
      const userCookie = Cookies.get('user')
      let token = null

      if (userCookie) {
        try {
          const u = JSON.parse(userCookie)
          token = u.token
        } catch (err) {
          console.error('Failed to parse user cookie', err)
        }
      }

      const headers = {
        'Content-Type': 'application/json',
        Authorization: token ? `Bearer ${token}` : undefined,
        'institute-Id': Number(institueId),
      }

      const response = await axios.get(BASE_URL + '/student/all', { headers })
      const allStudents = response.data.data || []
      setStudents(allStudents)
      setFilteredStudents(allStudents)
    } catch (error) {
      console.error('Error fetching students:', error)
      toast.error('Failed to fetch students')
    } finally {
      setLoading(false)
    }
  }

  const handleSelect = (selectedValues) => {
    setSelectedData(selectedValues)
    filterStudents(selectedValues)
  }

  const filterStudents = (filters) => {
    let filtered = [...students]

    if (filters.course) {
      filtered = filtered.filter((s) => s.course_id === parseInt(filters.course))
    }

    if (filters.class) {
      filtered = filtered.filter((s) => s.class_id === parseInt(filters.class))
    }

    if (filters.student) {
      const studentId = parseInt(filters.student)
      filtered = filtered.filter((s) => s.id === studentId)
    }

    if (search.trim()) {
      filtered = filtered.filter((student) =>
        Object.values(student).some(
          (value) =>
            value !== null &&
            value !== undefined &&
            value.toString().toLowerCase().includes(search.trim().toLowerCase()),
        ),
      )
    }

    setFilteredStudents(filtered)
  }

  useEffect(() => {
    filterStudents(selectedData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, selectedData, students])

  const handleStudentToggle = (studentId) => {
    setSelectedStudents((prev) => {
      if (prev.includes(studentId)) {
        return prev.filter((id) => id !== studentId)
      }
      return [...prev, studentId]
    })
  }

  const handleSelectAll = () => {
    if (selectedStudents.length === filteredStudents.length) {
      setSelectedStudents([])
    } else {
      setSelectedStudents(filteredStudents.map((s) => s.id))
    }
  }

  const handleAssignStudents = async () => {
    if (!bus) {
      toast.error('Bus information not found. Please go back and try again.')
      return
    }

    if (selectedStudents.length === 0) {
      toast.error('Please select at least one student')
      return
    }

    const instituteId = Cookies.get('institute_id')
    const userCookie = Cookies.get('user')
    let token = null

    if (userCookie) {
      try {
        const user = JSON.parse(userCookie)
        token = user?.token || null
      } catch (err) {
        console.error('Failed to parse user cookie', err)
      }
    }

    if (!token) {
      toast.error('Authentication required. Please login again.')
      return
    }

    setAssigning(true)
    try {
      const headers = {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
        'institute-Id': Number(instituteId),
      }

      const resolvedBusId = bus?.id ?? bus?.bus_id ?? null
      const requests = selectedStudents.map((studentId) =>
        axios.post(
          `${BASE_URL}/transport/assign-bus`,
          {
            student_id: Number(studentId),
            bus_id: resolvedBusId,
          },
          { headers },
        ),
      )

      const results = await Promise.allSettled(requests)
      const failed = results.filter((r) => r.status === 'rejected')
      const succeededCount = results.length - failed.length

      if (succeededCount > 0) {
        const assignedBusId = resolvedBusId
        setStudents((prev) =>
          prev.map((student) =>
            selectedStudents.includes(student.id)
              ? { ...student, assign_bus: assignedBusId }
              : student,
          ),
        )
      }

      if (failed.length === 0) {
        const message =
          results?.[0]?.status === 'fulfilled' &&
          results[0]?.value?.data?.message
            ? results[0].value.data.message
            : `Bus assigned successfully to ${succeededCount} student${
                succeededCount !== 1 ? 's' : ''
              }`
        toast.success(message)
        setSelectedStudents([])
        return
      }

      const firstError = failed[0]?.reason
      const backendMessage =
        firstError?.response?.data?.message ||
        firstError?.response?.data?.error ||
        firstError?.message

      if (succeededCount === 0) {
        toast.error(backendMessage || 'Failed to assign bus to selected students')
        return
      }

      toast(
        `Bus assigned to ${succeededCount} student${succeededCount !== 1 ? 's' : ''}, ${failed.length} failed${
          backendMessage ? ` (${backendMessage})` : ''
        }`,
        { icon: '⚠️' },
      )
      setSelectedStudents([])
    } catch (error) {
      console.error('Error assigning bus:', error)
      const errorMessage =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        'Failed to assign bus'
      toast.error(errorMessage)
    } finally {
      setAssigning(false)
    }
  }

  const handleRefresh = () => {
    setSelectedData({})
    setSearch('')
    setSelectedStudents([])
    setResetKey((prev) => prev + 1)
    fetchAllStudents()
  }

  return (
    <>
      <style>
        {`
          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        {/* Header */}
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src="/tta_logobg.png"
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>

                <div>
                  <h3
                    className="m-0 fw-bold text-start"
                    style={{
                      letterSpacing: '-0.02em',
                      overflow: 'visible',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      fontSize: 'clamp(18px, 2.5vw, 22px)',
                      color: '#111827',
                      lineHeight: '1.2',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Assign Student
                  </h3>
                  <p
                    className="m-0"
                    style={{
                      fontSize: '13px',
                      color: '#6b7280',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Bus: {bus?.busName || 'N/A'} ({bus?.busNumber || 'N/A'})
                  </p>
                </div>
              </div>

              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
            {selectedStudents.length > 0 && (
              <Button
                variant="success"
                onClick={handleAssignStudents}
                disabled={assigning}
                style={{
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                {assigning ? (
                  'Assigning...'
                ) : (
                  <>
                    <BsPersonPlusFill size={18} className="me-2" />
                    Assign {selectedStudents.length} Student
                    {selectedStudents.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            )}
          </div>

          <div
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'hidden',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5
                          className="m-0 fw-semibold text-start"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Filter Students
                        </h5>
                        <p className="m-0 mt-1" style={{ fontSize: '13px', color: '#6b7280' }}>
                          Filter students by course, class, or search by name
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleRefresh}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: 'pointer',
                          transition: 'all 0.2s ease',
                        }}
                        title="Refresh"
                      >
                        <BsFunnelFill size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body className="p-4" style={{ backgroundColor: '#ffffff' }}>
                  <Row className="g-3">
                    <Col xs={12}>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#223843',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BsFunnelFill style={{ color: '#ffffff', fontSize: '16px' }} />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Filter by Course, Class & Student
                          </label>
                        </div>
                        <SelectableField
                          onSelect={handleSelect}
                          showCourse={true}
                          showClass={true}
                          showStudent={true}
                          abc={'abc'}
                          setData={setFilteredStudents}
                          resetTrigger={resetKey}
                        />
                      </div>
                    </Col>

                    <Col xs={12}>
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: '#F3F4F6',
                          borderRadius: '8px',
                          border: '1px solid #D1D3D4',
                        }}
                      >
                        <div className="d-flex align-items-center gap-2 mb-3">
                          <div
                            style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              backgroundColor: '#10B981',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <BsSearch style={{ color: '#ffffff', fontSize: '16px' }} />
                          </div>
                          <label
                            className="fw-semibold mb-0"
                            style={{
                              color: '#111827',
                              fontSize: '14px',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            Search Student Name
                          </label>
                        </div>
                        <div style={{ position: 'relative' }}>
                          <BsSearch
                            style={{
                              position: 'absolute',
                              left: '12px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              color: '#6b7280',
                              fontSize: '16px',
                              zIndex: 1,
                            }}
                          />
                          <Form.Control
                            type="text"
                            placeholder="Enter student name..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            style={{
                              paddingLeft: '40px',
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              transition: 'all 0.2s ease',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          <Card
            style={{
              border: '1px solid #D1D3D4',
              borderRadius: '12px',
              overflow: 'hidden',
              backgroundColor: '#ffffff',
            }}
          >
            <SuperTable className="table mb-0" style={{ borderRadius: '12px', overflow: 'hidden' }}>
              <Thead>
                <Tr style={{ backgroundColor: '#F3F4F6' }}>
                  <Th
                    style={{
                      fontSize: '13px',
                      fontWeight: '600',
                      padding: '16px',
                      border: 'none',
                      borderBottom: '1px solid #D1D3D4',
                      textAlign: 'left',
                      color: '#111827',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      width: '50px',
                    }}
                  >
                    <Form.Check
                      type="checkbox"
                      checked={
                        filteredStudents.length > 0 &&
                        selectedStudents.length === filteredStudents.length
                      }
                      onChange={handleSelectAll}
                    />
                  </Th>
                  {['Admission Number', 'Name', "Father's Name", 'Email'].map((h) => (
                    <Th
                      key={h}
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      {h}
                    </Th>
                  ))}
                </Tr>
              </Thead>
              <Tbody>
                {loading ? (
                  <Tr>
                    <Td
                      colSpan="5"
                      className="text-center"
                      style={{
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Loading students...
                    </Td>
                  </Tr>
                ) : filteredStudents.length > 0 ? (
                  filteredStudents.map((student, index) => (
                    <Tr
                      key={student.id}
                      style={{
                        backgroundColor:
                          index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)',
                        transition: 'background-color 0.2s ease',
                        borderBottom: '1px solid #E5E7EB',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(37, 99, 235, 0.06)'
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor =
                          index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
                      }}
                    >
                      <Td
                        style={{
                          fontSize: '14px',
                          padding: '16px',
                          border: 'none',
                          textAlign: 'left',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        <Form.Check
                          type="checkbox"
                          checked={selectedStudents.includes(student.id)}
                          onChange={() => handleStudentToggle(student.id)}
                        />
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                        {student.admission_number || 'N/A'}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                        {student.name || 'N/A'}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                        {student.father_name || 'N/A'}
                      </Td>
                      <Td style={{ fontSize: '14px', padding: '16px', border: 'none', color: '#111827' }}>
                        {student.email || 'N/A'}
                      </Td>
                    </Tr>
                  ))
                ) : (
                  <Tr>
                    <Td
                      colSpan="5"
                      className="text-center"
                      style={{
                        padding: '40px',
                        fontSize: '14px',
                        color: '#6b7280',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      No students found
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </SuperTable>
          </Card>

          {selectedStudents.length > 0 && (
            <div
              className="mt-3"
              style={{
                padding: '12px 16px',
                backgroundColor: '#EFF6FF',
                borderRadius: '8px',
                border: '1px solid #223843',
              }}
            >
              <p
                className="m-0"
                style={{
                  fontSize: '14px',
                  color: '#223843',
                  fontFamily: 'Inter, system-ui, sans-serif',
                  fontWeight: '500',
                }}
              >
                <BsCheckCircleFill className="me-2" />
                {selectedStudents.length} student{selectedStudents.length !== 1 ? 's' : ''} selected
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default AssignBustoStudent


